import React, { useCallback, useState } from "react";
import { HStack } from "@chakra-ui/react";
import { Button as ShadcnButton } from "@/components/ui/button";
import type { SimulateLegs } from "../../../types/managementExplorer";
import type { ExitPlacementLegAvailability } from "../../../utils/exitPlacementSimLegs";
import { normalizeMandatorySimulatorRunTitle, NextRunTitleField } from "./simulatorRunTitleFields";
import { ExitPlacementSimLegSelector } from "./ExitPlacementSimLegSelector";

export type ExitPlacementPicksRunToolbarProps = {
  simulateLegs: SimulateLegs;
  onSimulateLegsChange: (legs: SimulateLegs) => void;
  legAvailability: ExitPlacementLegAvailability;
  simulateLegError: string | null;
  onClearSimulateLegError: () => void;
  selectedPickCount: number;
  importablePickCount: number;
  multiPickSelected: boolean;
  /** Show Run separately / Run selected pick (Single / Ran lane selections). */
  showSeparateRun?: boolean;
  /** Show Run picks combined (Combined lane selections). */
  showCombinedRun?: boolean;
  simulateAtInflightLimit: boolean;
  separateRunDisabled: boolean;
  combinedRunDisabled: boolean;
  picksCount: number;
  onClearPicks: () => void;
  onInsertIntoPolicyTree: () => void;
  onRunSeparate: (title: string) => void;
  onRunCombined: (title: string) => void;
  onCheckOverlap?: () => void;
  checkOverlapDisabled?: boolean;
  /** @deprecated No longer used — simulations enqueue immediately. */
  importingPicks?: boolean;
  /** @deprecated No longer used — simulations enqueue immediately. */
  pickRunLoadingAction?: "individual" | "combined" | null;
};

/** Picks run toolbar — title draft is local so typing does not re-render the whole simulator panel. */
export const ExitPlacementPicksRunToolbar = React.memo(function ExitPlacementPicksRunToolbar(
  props: ExitPlacementPicksRunToolbarProps
): React.ReactElement {
  const [titleDraft, setTitleDraft] = useState("");

  const clearTitleAfterRun = useCallback(() => setTitleDraft(""), []);

  const runSeparate = useCallback(() => {
    const title = normalizeMandatorySimulatorRunTitle(titleDraft);
    if (!title) return;
    try {
      props.onRunSeparate(title);
      clearTitleAfterRun();
    } catch {
      // Parent shows toast; keep draft for retry.
    }
  }, [clearTitleAfterRun, props, titleDraft]);

  const runCombined = useCallback(() => {
    const title = normalizeMandatorySimulatorRunTitle(titleDraft);
    if (!title) return;
    try {
      props.onRunCombined(title);
      clearTitleAfterRun();
    } catch {
      // Parent shows toast; keep draft for retry.
    }
  }, [clearTitleAfterRun, props, titleDraft]);

  const legDisabled =
    props.simulateAtInflightLimit ||
    props.importablePickCount === 0 ||
    props.selectedPickCount === 0;

  return (
    <HStack
      align="flex-end"
      flexWrap="wrap"
      gap={3}
      w="100%"
      rowGap={3}
      justify="space-between"
      mb={4}
      data-simulator-run-toolbar=""
    >
      <HStack spacing={3} align="flex-end" flexWrap="wrap" flex="0 1 auto" minW={0}>
        <NextRunTitleField id="picks-run-title" value={titleDraft} onChange={setTitleDraft} />
        <ExitPlacementSimLegSelector
          id="picks-run-simulate-legs"
          variant="picks"
          value={props.simulateLegs}
          onChange={(v) => {
            props.onSimulateLegsChange(v);
            props.onClearSimulateLegError();
          }}
          availability={props.legAvailability}
          disabled={legDisabled}
          inlineError={props.simulateLegError}
        />
      </HStack>

      <HStack spacing={2} flexWrap="wrap" align="flex-end" flexShrink={0}>
        <ShadcnButton
          type="button"
          variant="outline"
          size="sm"
          data-font="primary"
          data-trade-log-action="delete"
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={props.onClearPicks}
          disabled={props.picksCount === 0}
        >
          Clear picks
        </ShadcnButton>
        <ShadcnButton
          type="button"
          variant="outline"
          size="sm"
          data-font="primary"
          data-market-context-bulk-toggle=""
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={props.onInsertIntoPolicyTree}
          disabled={props.selectedPickCount === 0}
        >
          Insert into policy tree
        </ShadcnButton>
        {props.onCheckOverlap && (props.showCombinedRun ?? props.multiPickSelected) ? (
          <ShadcnButton
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-market-context-bulk-toggle=""
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={props.onCheckOverlap}
            disabled={props.checkOverlapDisabled}
          >
            Check overlap
          </ShadcnButton>
        ) : null}
        {(props.showSeparateRun ?? true) ? (
          <ShadcnButton
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={runSeparate}
            disabled={props.separateRunDisabled}
          >
            {props.multiPickSelected ? "Run picks separately" : "Run selected pick"}
          </ShadcnButton>
        ) : null}
        {(props.showCombinedRun ?? props.multiPickSelected) ? (
          <ShadcnButton
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={runCombined}
            disabled={props.combinedRunDisabled}
          >
            Run picks combined
          </ShadcnButton>
        ) : null}
      </HStack>
    </HStack>
  );
});
